import { useCallback, useState } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { useGetCurrentPlayerToken } from 'src/store/player'
import { getCashoutApiBases } from 'src/utils/gameApiUrl'
import { fetchWithNetworkRetry, CASHOUT_RETRY } from 'src/utils/cashoutRetry'

interface CashoutBalance {
  playerId: string
  amount: number
  currency: string
}

interface CashoutData {
  cashoutMultiplier: number
  actualPayout: number
  balance?: CashoutBalance
}

// Machine-readable rejection classes — the crash panel's optimistic-cashout
// rollback branches on these. Mapped from HTTP status + the backend's STABLE
// `error`/`details` strings (marbles-stack-go MapToErrorResponse ships no
// numeric `code` on cashout bodies yet); a reworded message degrades to
// 'unknown', never to a wrong class.
export type CashoutErrorKind =
  | 'after-crash' // 409 CashoutAfterCrashError — the crash beat the tap
  | 'not-pending' // 400 BetNotPendingForCashoutError — already settled/cashed/voided
  | 'round-inactive' // 400 RoundNotCrashActiveError — round settled/cancelled
  | 'before-climb' // 500 fallthrough, CashoutBeforeClimbError (details only)
  | 'session' // 401
  | 'not-found' // 404 bet/round
  | 'network' // fetch threw — no HTTP status at all
  | 'unknown'

export function mapCashoutErrorKind(
  status: number | undefined,
  error?: string,
  details?: string
): CashoutErrorKind {
  if (status === undefined) return 'network'
  if (status === 401) return 'session'
  if (status === 404) return 'not-found'
  // The stable string wins over the status so a proxy rewriting 409s (the
  // WAF fronts this API) can't misroute the one rejection that needs the
  // gentle treatment.
  if (status === 409 || /too late/i.test(error ?? '')) return 'after-crash'
  if (/not eligible for cashout/i.test(error ?? '')) return 'not-pending'
  if (/round not active for cashout/i.test(error ?? '')) return 'round-inactive'
  // CashoutBeforeClimbError has no MapToErrorResponse entry — it arrives as
  // a 500 "Internal server error" whose details carry the real reason.
  if (/has not started climbing/i.test(details ?? '')) return 'before-climb'
  return 'unknown'
}

interface CashoutResult {
  success: boolean
  data?: CashoutData
  error?: string
  errorKind?: CashoutErrorKind
}

interface CashoutResponse {
  cashoutMultiplier: number
  actualPayout: number
  balance: {
    playerId: string
    amount: number
    currency: string
  }
}

/**
 * Hook for cashing out crash game bets.
 *
 * Calls the public-game-service cashout endpoint which:
 * 1. Validates the bet is active and round is in climbing phase
 * 2. Calculates payout based on current multiplier
 * 3. Registers win with the provider
 * 4. Updates bet status to cashed_out
 * 5. Publishes BetCashedOutEvent for realtime updates
 */
export function useCashout() {
  const token = useGetCurrentPlayerToken()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cashout = useCallback(
    async (betId: string, roundId: string, gameId: string): Promise<CashoutResult> => {
      if (!token) {
        return { success: false, error: 'Not authenticated', errorKind: 'session' }
      }

      setIsLoading(true)
      setError(null)

      const pageHost = typeof window === 'undefined' ? undefined : window.location.hostname
      // Outside the try so the catch can attribute which base finally failed.
      let attemptedBase: string | undefined

      try {
        // Mirror-domain gap (hub88 SF-002189753): on rotated domains the page,
        // socket and bets all follow the mirror host, so the REST base must
        // too — a blocked-ISP player can only reach api.<mirror zone>. The env
        // base stays as fallback for zones whose api record isn't provisioned
        // yet (fails fast with NXDOMAIN, so the retry costs little).
        const apiBases = getCashoutApiBases(pageHost, process.env.NEXT_PUBLIC_GAME_API_URL)
        if (apiBases.length === 0) {
          throw new Error('NEXT_PUBLIC_GAME_API_URL not configured')
        }

        let response: Response | undefined
        for (const [index, apiBase] of apiBases.entries()) {
          const isLastBase = index === apiBases.length - 1
          attemptedBase = apiBase
          try {
            // Each base gets CASHOUT_RETRY: up to 3 bounded attempts, retried
            // ONLY when fetch throws (network class). On the primary domain
            // this loop has a single base, and before the retry existed that
            // meant one unbounded attempt whose first failure was terminal —
            // the player became the retry loop by hand (40 taps across two
            // rounds, hub88 Acebet 2026-09-04; ~100 sessions / 8 days).
            //
            // A blocked host can BLACKHOLE (TR-style interference drops
            // packets rather than NXDOMAINing), so EVERY attempt is bounded —
            // including the last one on the last base, which the old code
            // left unbounded and which on the primary ate the whole cash-out
            // window. Money-safe: a timed-out POST that did reach the server
            // settles server-side (the pending->cashed_out write is
            // conditional, single-winner); the retry then gets 'not-pending'
            // and the panel reconciles via fetchBets — no double payout. An
            // HTTP response of any status is never retried: the server
            // answered.
            response = await fetchWithNetworkRetry(
              fetch,
              `${apiBase}/bets/${betId}/cashout`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ roundId, gameId })
              },
              CASHOUT_RETRY,
              {
                onRetry: ({ attempt, error, delayMs }) => {
                  // Observable retry path — a spike here is the signal that the
                  // ~100-session class is still live; its ABSENCE alongside
                  // cashout_failed means a hard failure, not a blip.
                  datadogLogs.logger.warn('cashout_retry', {
                    host: pageHost,
                    apiBase,
                    attempt,
                    delayMs,
                    betId,
                    roundId,
                    errorName: error instanceof Error ? error.name : typeof error
                  })
                }
              }
            )
            break
          } catch (fetchErr) {
            // Network-level failure (DNS, TLS, ISP block, timeout) — any HTTP
            // response, even an error, would have resolved instead. Try the
            // next base; rethrow from the last so the outer catch keeps its
            // behavior.
            if (isLastBase) throw fetchErr
            datadogLogs.logger.warn('cashout_api_base_unreachable', {
              host: pageHost,
              apiBase,
              nextBase: apiBases[index + 1],
              betId,
              roundId
            })
          }
        }
        if (!response) {
          throw new Error('Cashout failed')
        }
        if (attemptedBase !== apiBases[0]) {
          datadogLogs.logger.warn('cashout_api_fallback_used', {
            host: pageHost,
            failedBase: apiBases[0],
            usedBase: attemptedBase,
            betId,
            roundId
          })
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          const errorMessage =
            errorData.message || errorData.error || `Cashout failed: ${response.status}`
          const errorKind = mapCashoutErrorKind(response.status, errorData.error, errorData.details)

          // Until this event existed, failed manual cashouts were console-only
          // and structurally invisible in Datadog — the mirror-domain outage
          // produced zero signal. host is the discriminator for that class.
          datadogLogs.logger.warn('cashout_failed', {
            errorKind,
            host: pageHost,
            apiBase: attemptedBase,
            status: response.status,
            betId,
            roundId,
            error: errorData.error
          })

          if (response.status === 401) {
            setError('Session expired')
            return { success: false, error: 'Session expired', errorKind }
          }
          if (response.status === 404) {
            setError('Bet not found')
            return { success: false, error: 'Bet not found', errorKind }
          }

          setError(errorMessage)
          return { success: false, error: errorMessage, errorKind }
        }

        const data: CashoutResponse = await response.json()

        return {
          success: true,
          data: {
            cashoutMultiplier: data.cashoutMultiplier,
            actualPayout: data.actualPayout,
            balance: data.balance
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Cashout failed'
        datadogLogs.logger.warn('cashout_failed', {
          errorKind: 'network',
          host: pageHost,
          apiBase: attemptedBase,
          betId,
          roundId,
          error: errorMessage
        })
        setError(errorMessage)
        return { success: false, error: errorMessage, errorKind: 'network' }
      } finally {
        setIsLoading(false)
      }
    },
    [token]
  )

  return {
    cashout,
    isLoading,
    error,
    clearError: () => setError(null)
  }
}
